import {Card, Container, Group, Menu, rem, Space, Tabs, Text, Title, UnstyledButton} from "@mantine/core";
import {useLocation, useNavigate} from "react-router-dom";
import {PropsWithChildren, useCallback} from "react";
import {TrailerEntity} from "@ots-frontend/ots-data";
import {TrailerDetails} from "./TrailerDetails";
import {createContentStyles} from "../../../theme/useContentStyles";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {TrailerAction, TrailerActionMenuDropdown} from "../../menu/TrailerActionMenu/TrailerActionMenu";
import {useTrailerMutation} from "../../../hooks/useTrailerMutation";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {ObjectChangeLogView} from "../common/ObjectChangeLogView/ObjectChangeLogView";
import {TrailerDVIRView} from "./TrailerDVIRView";

const useStyles = createContentStyles((theme) => ({
  mainSection: {
    paddingBottom: theme.spacing.sm
  }
}));

export type TrailerProfileProps = PropsWithChildren<{
  trailer: TrailerEntity
}>

const tabs = [
  {
    key: "details",
    value: "Details"
  },
  {
    key: "dvirs",
    value: "DVIRs",
    permissions: ["dvir_view"]
  },
  {
    key: "history",
    value: "Change Logs",
    permissions: ["change_log_view"]
  },
];

export const TrailerProfile = (props: TrailerProfileProps) => {
  const {trailer} = props;

  const {classes} = useStyles();

  const {t} = useTranslation()
  const navigate = useNavigate();
  const {pathname, search, state} = useLocation();

  const {
    deleteTrailer
  } = useTrailerMutation()

  const onActionClick = useCallback((action: TrailerAction) => {
    if (action === 'EDIT') {
      navigate(`/trailers/form`, {state: {id: trailer.id}})
    } else if (action === 'DELETE') {
      deleteTrailer(trailer.id, {
        onSuccess: () => {
          navigate(-1)
        }
      })
    }
  }, [trailer, navigate, deleteTrailer])

  const items = tabs.map((tab) => (
    <HasAccess accessOnEmpty={true} permissions={tab.permissions} key={tab.key}>
      <Tabs.Tab value={tab.key}>
        {tab.value}
      </Tabs.Tab>
    </HasAccess>
  ));

  return (
    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
      <Container fluid={true} className={classes.mainSection}>
        <Group position="apart">
          <Title order={4}>Trailer: {trailer.name}</Title>

          <Menu
            width={260}
            position="bottom-end"
            transitionProps={{transition: "pop-top-right"}}
            withinPortal
          >
            <Menu.Target>
              <UnstyledButton>
                <Group spacing={7}>
                  <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                    {t("common.action.plural", "Actions")}
                  </Text>
                  <IconChevronDown size={rem(12)} stroke={1.5}/>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <TrailerActionMenuDropdown trailer={trailer} onActionClick={onActionClick}/>
            </Menu.Dropdown>
          </Menu>

        </Group>
      </Container>

      <Card.Section>
        <Container fluid={true}>
          <Tabs
            defaultValue={state?.tabValue ?? "details"}
            onTabChange={(value) => navigate(pathname + search, {replace: true, state: {...state, tabValue: value}})}
          >
            <Tabs.List>{items}</Tabs.List>
            <Space h="md"/>
            <Tabs.Panel value="details">
              <TrailerDetails trailer={trailer}/>
            </Tabs.Panel>
            <Tabs.Panel value="dvirs">
              <TrailerDVIRView trailer={trailer}/>
            </Tabs.Panel>
            <Tabs.Panel value="history">
              <ObjectChangeLogView entity={trailer}/>
            </Tabs.Panel>
          </Tabs>
        </Container>
      </Card.Section>
    </Card>
  );
};
